import React, { useState } from 'react';
import './styles.scss';
import { Experience } from '../../../../../context/dataExperienceContext';
import { LocationWithData, PickupLocation } from '../../../../../context/viator-product-types';
import Dropdown from '../../../../../components/Dropdown';
import { faLocationDot, faFlagCheckered, faUserGroup, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandableSection from '../../../../../components/ExpandableSection';

interface SelectProps {
  options: any[];
  placeholder?: string;
  onSelect: (value: any, index: number) => void;
  selectedIndex: number | null;
  renderOption: (option: any, index: number) => React.ReactNode;
  renderSelected: (option: any | null) => React.ReactNode;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  emptyMessage?: string;
}

/**
 * Select component for dropdown selection UI pattern
 */
const Select: React.FC<SelectProps> = ({
  options,
  placeholder = 'Selecciona una opción',
  onSelect,
  selectedIndex,
  renderOption,
  renderSelected,
  searchable = false,
  onSearch,
  searchQuery = '',
  emptyMessage = 'No hay opciones disponibles'
}) => {
  const selectedOption = selectedIndex !== null ? options[selectedIndex] : null;

  const handleOptionClick = (option: any, index: number) => {
    onSelect(option, index);
  };

  return (
    <Dropdown
      trigger={['click']}
      dropdownRender={
        <div className="select-dropdown-content">
          {searchable && onSearch && (
            <div className="select-search-container">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Buscar..."
                className="select-search-input"
              />
            </div>
          )}
          <div className="select-options-list">
            {options.length > 0 ? (
              options.map((option, index) => (
                <div
                  key={`option-${index}`}
                  className={`select-option ${selectedIndex === index ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option, index)}
                >
                  {renderOption(option, index)}
                </div>
              ))
            ) : (
              <div className="select-empty-message">{emptyMessage}</div>
            )}
          </div>
        </div>
      }
    >
      <div className="select-trigger">
        {selectedOption ? renderSelected(selectedOption) : <div className="select-placeholder">{placeholder}</div>}
      </div>
    </Dropdown>
  );
};

interface GoogleMapsLinkProps {
  url: string;
}

const GoogleMapsLink: React.FC<GoogleMapsLinkProps> = ({ url }) => (
  <a href={url} target="_blank" rel="noreferrer" className="google-maps-link">
    Abrir en Google Maps
    <FontAwesomeIcon icon={faChevronRight} />
  </a>
);

interface LocationDetailsProps {
  address: string | null;
  description?: string | null;
  googleMapsUrl: string | null;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({ address, description, googleMapsUrl }) => (
  <div className="location-details">
    <div className="location-address">{address}</div>
    {googleMapsUrl && <GoogleMapsLink url={googleMapsUrl} />}
    {description && <div className="location-description">{description}</div>}
  </div>
);

interface LocationSelectProps {
  locations: any[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  placeholder: string;
  emptyMessage: string;
  renderOption: (location: any) => React.ReactNode;
  renderSelected: (location: any) => React.ReactNode;
  searchable?: boolean;
  searchQuery?: string;
  onSearch?: (query: string) => void;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  locations,
  selectedIndex,
  onSelect,
  placeholder,
  emptyMessage,
  renderOption,
  renderSelected,
  searchable = false,
  searchQuery = '',
  onSearch
}) => (
  <Select
    options={locations}
    placeholder={placeholder}
    selectedIndex={selectedIndex}
    onSelect={(_, index) => onSelect(index)}
    renderOption={(location) => renderOption(location)}
    renderSelected={(location) => renderSelected(location)}
    searchable={searchable}
    searchQuery={searchQuery}
    onSearch={onSearch}
    emptyMessage={emptyMessage}
  />
);

// Common render functions for location options
const renderLocationOption = (location: any) => {
  const address = location.location.data.address;
  const name = location.location.data.name;

  if (address) {
    const [title, ...rest] = address.split(',');
    return (
      <>
        <div className="location-type">
          <strong>{name || title}</strong>
        </div>
        {rest.length > 0 && (
          <div className="location-address">
            {rest.join(',')}
          </div>
        )}
      </>
    );
  }

  if (name) {
    return (
      <div className="location-type">
        {name}
      </div>
    );
  }

  return null;
};

const renderSelectedLocation = (location: any) => (
  <div className="location-type">
    {location.location.data.name || location.location.data.address}
  </div>
);

/**
 * Logistics component displays trip starting points, ending points, and potential pickup options for experiences/tours
 */
const Logistics = ({ experience }: { experience: Experience }) => {
  // Type assertion to handle logistics which may exist but not be in the type
  const logistics = experience.product.logistics;
  const [selectedPickupIndex, setSelectedPickupIndex] = useState<number | null>(null);
  const [selectedEndIndex, setSelectedEndIndex] = useState<number | null>(null);
  const [selectedStartIndex, setSelectedStartIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  if (!logistics) return null;

  // Helper function to create Google Maps URL
  const createGoogleMapsUrl = (location: LocationWithData): string | null => {
    if (location?.data.address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.data.address)}`;
    } else {
      return null;
    }
  };

  // Render the starting point information
  const renderStartingPoint = () => {
    if (!logistics.start || logistics.start.length === 0) return null;

    // If there's only one start location, display it directly
    if (logistics.start.length === 1) {
      const startLocation = logistics.start[0];
      const address = startLocation.location.data.address;
      const googleMapsUrl = createGoogleMapsUrl(startLocation.location);

      return (
        <>
          <div className="logistics-divider"></div>
          <div className="logistics-section">
            <div className="logistics-section-header">
              <div className="logistics-icon">
                <FontAwesomeIcon icon={faLocationDot} className="start-icon" />
              </div>
              <h3>Punto de Encuentro</h3>
            </div>
            <div className="logistics-content">

              <LocationDetails
                address={address}
                description={startLocation.description}
                googleMapsUrl={googleMapsUrl}
              />
            </div>
          </div>
        </>
      );
    }

    const selectedLocation = selectedStartIndex !== null ? logistics.start[selectedStartIndex] : null;
    const selectedLocationMapUrl = selectedLocation ? createGoogleMapsUrl(selectedLocation.location) : null;

    return (
      <>
        <div className="logistics-divider"></div>
        <div className="logistics-section">
          <div className="logistics-icon">
            <FontAwesomeIcon icon={faLocationDot} className="start-icon" />
          </div>
          <div className="logistics-content">
            <h3>Punto de Encuentro</h3>
            <div className="start-selector">
              <LocationSelect
                locations={logistics.start}
                selectedIndex={selectedStartIndex}
                onSelect={setSelectedStartIndex}
                placeholder="Selecciona un punto de encuentro"
                emptyMessage="No hay puntos de encuentro disponibles"
                renderOption={renderLocationOption}
                renderSelected={renderSelectedLocation}
              />
              {selectedLocation && (
                <LocationDetails
                  address={selectedLocation.location.data.address}
                  description={selectedLocation.description}
                  googleMapsUrl={selectedLocationMapUrl}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  // Render the ending point information
  const renderEndingPoint = () => {
    if (!logistics.end || logistics.end.length === 0) return null;

    // If there's only one end location, display it directly
    if (logistics.end.length === 1) {
      const endLocation = logistics.end[0];

      // Check if end location is same as start location when both have single locations
      if (logistics.start?.length === 1 && endLocation.location.ref === logistics.start[0].location.ref) {
        return (
          <>
            <div className="logistics-divider"></div>
            <div className="logistics-section">
              <div className="logistics-section-header">
                <div className="logistics-icon">
                  <FontAwesomeIcon icon={faFlagCheckered} className="end-icon" />
                </div>
                <h3>Punto de finalización</h3>
              </div>
              <div className="logistics-content">
                <p className='location-info'>Esta actividad finaliza en el punto de encuentro inicial.</p>
              </div>
            </div>
          </>
        );
      }

      const googleMapsUrl = createGoogleMapsUrl(endLocation.location);
      const address = endLocation.location.data.address;

      return (
        <>
          <div className="logistics-divider"></div>
          <div className="logistics-section">
            <div className="logistics-section-header">
              <div className="logistics-icon">
                <FontAwesomeIcon icon={faFlagCheckered} className="end-icon" />
              </div>
              <h3>Punto de finalización</h3>
            </div>
            <div className="logistics-content">
            <p className='location-info'>Esta actividad finaliza en una ubicación distinta. Consulta los detalles a continuación:</p>
              <LocationDetails
                address={address}
                description={endLocation.description}
                googleMapsUrl={googleMapsUrl}
              />
            </div>
          </div>
        </>
      );
    }

    const selectedLocation = selectedEndIndex !== null ? logistics.end[selectedEndIndex] : null;
    const selectedLocationMapUrl = selectedLocation ? createGoogleMapsUrl(selectedLocation.location) : null;

    return (
      <>
        <div className="logistics-divider"></div>
        <div className="logistics-section">
          <div className="logistics-section-header">
            <div className="logistics-icon">
              <FontAwesomeIcon icon={faFlagCheckered} className="end-icon" />
            </div>
            <h3>Punto de finalización</h3>
          </div>
          <div className="logistics-content">
            <div className="end-selector">
              <p className='location-info'>Esta actividad finaliza en una ubicación distinta. Consulta los detalles a continuación:</p>
              <LocationSelect
                locations={logistics.end}
                selectedIndex={selectedEndIndex}
                onSelect={setSelectedEndIndex}
                placeholder="Escriba para buscar"
                emptyMessage="No hay puntos de finalización disponibles"
                renderOption={renderLocationOption}
                renderSelected={renderSelectedLocation}
              />
              {selectedLocation && (
                <LocationDetails
                  address={selectedLocation.location.data.address}
                  description={selectedLocation.description}
                  googleMapsUrl={selectedLocationMapUrl}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  // Render pickup locations if available
  const renderPickupLocations = () => {
    if (!logistics.travelerPickup?.locations || logistics.travelerPickup.locations.length === 0) return null;

    const pickupLocations = logistics.travelerPickup.locations;
    const filteredLocations = pickupLocations.filter((loc: PickupLocation) =>
      !searchQuery || loc.location.data.address?.toLowerCase().includes(searchQuery.toLowerCase()) || loc.location.data.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const selectedLocation = selectedPickupIndex !== null ? pickupLocations[selectedPickupIndex] : null;
    const selectedLocationMapUrl = selectedLocation ? createGoogleMapsUrl(selectedLocation.location) : null;

    return (
      <>
        <div className="logistics-divider"></div>
        <div className="logistics-section">
          <div className="logistics-section-header">
            <div className="logistics-icon">
            <FontAwesomeIcon icon={faUserGroup} className="pickup-icon" />
            </div>
            <h3>Puntos de Recogida</h3>
          </div>
          <div className="logistics-content">
            <div className="pickup-selector">
              <LocationSelect
                locations={filteredLocations}
                selectedIndex={selectedPickupIndex}
                onSelect={(index) => setSelectedPickupIndex(index)}
                placeholder="Selecciona un punto de recogida"
                emptyMessage="No hay puntos de recogida que coincidan con tu búsqueda."
                renderOption={renderLocationOption}
                renderSelected={renderSelectedLocation}
                searchable={pickupLocations.length > 1}
                searchQuery={searchQuery}
                onSearch={setSearchQuery}
              />
              {selectedLocation && (
                <LocationDetails
                  address={selectedLocation.location.data.address}
                  googleMapsUrl={selectedLocationMapUrl}
                />
              )}
            </div>
            {logistics.travelerPickup.additionalInfo && (
              <div className="pickup-additional-info">
                <h4>Información Adicional</h4>
                <p>{logistics.travelerPickup.additionalInfo}</p>
              </div>
            )}
            {logistics.travelerPickup.minutesBeforeDepartureTimeForPickup && (
              <div className="pickup-timing">
                <h4>Horario de Recogida</h4>
                <p>La recogida es {logistics.travelerPickup.minutesBeforeDepartureTimeForPickup} minutos antes de la hora de salida.</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <ExpandableSection title="Encuentro y recogida" defaultExpanded={true}>
      <div className="logistics-content-wrapper">
        {renderStartingPoint()}
        {renderEndingPoint()}
        {renderPickupLocations()}
      </div>
    </ExpandableSection>
  );
};

export default Logistics;